import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import GenericTable from "../GenericTable/GenericTable";
import { CircularProgress, Alert } from "@mui/material";
import config from "../../../config";
import { AuthContext } from "../../../authentication/AuthContext";

const DEFAULT_LIMIT = 100;

const ChatColumns = [
  {
    id: "customer_name",
    label: "Customer Name",
    render: (row) =>
      row.customer_name ? row.customer_name : "N/A",
  },
  {
    id: "store_name",
    label: "Store Name",
    render: (row) =>
      row.store_name ? row.store_name : "N/A",
  },
  {
    id: "last_purchased_product",
    label: "Last Purchased Product",
    render: (row) =>
      row.last_purchased_product ? row.last_purchased_product : "N/A",
  },
  {
    id: "top_mood",
    label: "Top Mood",
    render: (row) =>
      Array.isArray(row.top_mood)
    ? row.top_mood.join(", ")
    : row.top_mood || "N/A",
  },
  {
    id: "chat_overview",
    label: "Chat Overview",
    render: (row) =>
      row.chat_overview ? row.chat_overview : "N/A",
  },
  {
    id: "active_time",
    label: "Active Time (mins)",
    render: (row) =>
      row.active_time !== null && row.active_time !== undefined
    ? row.active_time
    : "N/A",
  },
  {
    id: "last_visit",
    label: "Last Visit",
    render: (row) => {
      if (!row.last_visit) return "N/A";
      const d = new Date(row.last_visit);
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      const seconds = String(d.getSeconds()).padStart(2, "0");
      return `${month}-${day}-${year} ${hours}:${minutes}:${seconds}`;
    },
  },
];

function ChatTranscriptsList() {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchChatTranscripts = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if user is authenticated
        if (!config.isAuthenticated()) {
          logout("Please login to access chat transcripts.");
          return;
        }

        console.log("Making API call with:", {
          clientId: config.clientId,
          apiKey: config.apiKey ? "***" + config.apiKey.slice(-4) : "missing"
        });

        const response = await fetch(
          `${config.API_BASE_URL}/client/list?client_id=${config.clientId}&page=1&limit=${DEFAULT_LIMIT}`,
          {
            method: "GET",
            headers: config.getHeaders(),
          }
        );

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error("Session expired. Please login again.");
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result?.success && result?.data?.chat_sessions) {
          setRows(result.data.chat_sessions);
        } else {
          setError("No chat transcripts found.");
        }
      } catch (err) {
        console.error("Error fetching chat transcripts:", err);
        if (err.message.includes("Session expired") || err.message.includes("login")) {
          logout(err.message);
        } else {
          setError(err.message || "Failed to fetch chat transcripts. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchChatTranscripts();
  }, [logout]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ margin: "2rem" }}>
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  return (
    <GenericTable
      title="Chat Transcript List"
      columns={ChatColumns}
      rows={rows}
      showView={true}
      showEdit={false}
      showDelete={false}
      dropdownFilters={["store_name", "top_mood"]}
      onView={(row) => {
        navigate(`/chat-transcripts/chats/${row.store_customer_id}`);
      }}
    />
  );
}

export default ChatTranscriptsList;